import { Box, Typography } from '@mui/material';
import { tokens } from '../../theme';

interface StatusChipProps {
  status: string;
  size?: 'small' | 'medium';
}

interface StatusConfig {
  label: string;
  dot: string;
  bg: string;
  text: string;
}

const { success, warning, danger, info, neutral } = tokens.status;

const CONFIG: Record<string, StatusConfig> = {
  ACTIVE:           { label: 'Active',          dot: success.dot, bg: success.bg, text: success.text },
  INACTIVE:         { label: 'Inactive',         dot: neutral.dot, bg: neutral.bg, text: neutral.text },
  SUSPENDED:        { label: 'Suspended',        dot: danger.dot,  bg: danger.bg,  text: danger.text },
  PENDING:          { label: 'Pending',          dot: warning.dot, bg: warning.bg, text: warning.text },
  IN_PROGRESS:      { label: 'In Progress',      dot: info.dot,    bg: info.bg,    text: info.text },
  COMPLETED:        { label: 'Completed',        dot: success.dot, bg: success.bg, text: success.text },
  MISSED:           { label: 'Missed',           dot: danger.dot,  bg: danger.bg,  text: danger.text },
  CANCELLED:        { label: 'Cancelled',        dot: neutral.dot, bg: neutral.bg, text: neutral.text },
  ARRIVED:          { label: 'Arrived',          dot: info.dot,    bg: info.bg,    text: info.text },
  WORKING:          { label: 'Working',          dot: success.dot, bg: success.bg, text: success.text },
  STARTED:          { label: 'Started',          dot: info.dot,    bg: info.bg,    text: info.text },
  ENDED:            { label: 'Ended',            dot: neutral.dot, bg: neutral.bg, text: neutral.text },
  NOT_STARTED:      { label: 'Not Started',      dot: neutral.dot, bg: neutral.bg, text: neutral.text },
  ABSENT:           { label: 'Absent',           dot: danger.dot,  bg: danger.bg,  text: danger.text },
  EXPIRED:          { label: 'Expired',          dot: danger.dot,  bg: danger.bg,  text: danger.text },
  LOW:              { label: 'Low',              dot: neutral.dot, bg: neutral.bg, text: neutral.text },
  MEDIUM:           { label: 'Medium',           dot: warning.dot, bg: warning.bg, text: warning.text },
  HIGH:             { label: 'High',             dot: danger.dot,  bg: danger.bg,  text: danger.text },
};

export function StatusChip({ status, size = 'small' }: StatusChipProps) {
  const cfg = CONFIG[status] ?? {
    label: status.replace(/_/g, ' '),
    dot: neutral.dot,
    bg: neutral.bg,
    text: neutral.text,
  };

  const isSmall = size === 'small';

  return (
    <Box
      component="span"
      role="status"
      aria-label={cfg.label}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: isSmall ? '5px' : '6px',
        px: isSmall ? '8px' : '10px',
        py: isSmall ? '3px' : '5px',
        borderRadius: '6px',
        backgroundColor: cfg.bg,
        userSelect: 'none',
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          width: isSmall ? 6 : 7,
          height: isSmall ? 6 : 7,
          borderRadius: '50%',
          backgroundColor: cfg.dot,
          flexShrink: 0,
        }}
      />
      <Typography
        component="span"
        sx={{
          fontSize: isSmall ? '0.72rem' : '0.8125rem',
          fontWeight: 600,
          color: cfg.text,
          letterSpacing: '0.01em',
          lineHeight: 1,
        }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
}

export default StatusChip;
